// Admin AI Artwork. Read-only inspector for the pre-configured prompt
// that the customer-facing /designer "Generate with AI" tab wraps around
// the customer's text before posting to FAL.
//
// The page reads /api/admin/ai-artwork/prompt so the displayed prompt is
// always the EXACT string the running route builds — never a copy that
// could drift from the code. Three variants are returned (one per
// garment-tone class: dark / light / mid); the page renders them as a
// tab strip so the operator can compare palette-swap output.
import { useEffect, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch, useApiSrc } from '../../lib/api'

interface Variant {
  tone: 'dark' | 'light' | 'mid' | 'pinned-palette'
  example: { garmentName: string; garmentColourName: string; garmentColourHex: string }
  prompt: string
}
interface PromptResp {
  model: string
  bgRemovalModel: string
  bgRemovalVariant: string
  resolution: string
  aspectRatio: string
  backdropHex: string
  variants: Variant[]
}
interface LeaderUser {
  uploaderId: string | null
  name: string
  email: string | null
  count: number
  lastAt: string
}
interface LeaderResp { total: number; users: LeaderUser[] }
interface UserFile {
  id: string
  originalName: string
  mimeType: string | null
  widthPx: number | null
  heightPx: number | null
  size: number
  aiPrompt: string | null
  createdAt: string
  url: string
}
interface UserFilesResp { items: UserFile[] }

export default function AiArtwork() {
  const { getAccessToken } = useAuth()
  const apiSrc = useApiSrc()
  const [data, setData] = useState<PromptResp | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [tab, setTab] = useState<'dark' | 'light' | 'mid' | 'pinned-palette'>('dark')
  const [copied, setCopied] = useState(false)
  // Leaderboard + selected user gallery state
  const [leader, setLeader] = useState<LeaderResp | null>(null)
  const [selectedUser, setSelectedUser] = useState<LeaderUser | null>(null)
  const [gallery, setGallery] = useState<UserFile[]>([])
  const [galleryLoading, setGalleryLoading] = useState(false)

  useEffect(() => {
    void (async () => {
      try {
        const [d, l] = await Promise.all([
          apiFetch<PromptResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/ai-artwork/prompt'),
          apiFetch<LeaderResp>(getAccessToken as unknown as () => Promise<string | undefined>, '/api/admin/ai-artwork/users'),
        ])
        setData(d); setLeader(l); setErr(null)
      } catch (e) { setErr((e as Error).message) }
    })()
  }, [getAccessToken])

  // Load a user's AI gallery on click. Re-fetches every time so a fresh
  // visit always reflects the latest generations the customer has made.
  async function openUser(u: LeaderUser) {
    setSelectedUser(u); setGallery([]); setGalleryLoading(true)
    try {
      const key = u.uploaderId ?? 'guest'
      const r = await apiFetch<UserFilesResp>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        `/api/admin/ai-artwork/users/${encodeURIComponent(key)}/files`,
      )
      setGallery(r.items)
    } catch (e) { setErr((e as Error).message) }
    finally { setGalleryLoading(false) }
  }

  if (err)   return <div className="p-8"><div className="card text-red-700">Error: {err}</div></div>
  if (!data) return <div className="p-8 text-ink-3 text-sm">Loading…</div>

  const variant = data.variants.find((v) => v.tone === tab) ?? data.variants[0]!

  async function copyPrompt() {
    try {
      await navigator.clipboard.writeText(variant.prompt)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch { /* clipboard refused — ignore */ }
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-6">
        <div className="eyebrow">Catalog</div>
        <h1 className="font-heading uppercase font-bold text-3xl text-ink">AI Artwork</h1>
        <p className="text-ink-2 text-sm mt-2 max-w-3xl">
          This page shows the <strong className="text-ink">system prompt</strong> the customer-facing <em>Generate with AI</em> tab wraps around the customer's text before sending it to FAL. The prompt is rebuilt on every request from the running code — what you see here is exactly what's being sent.
        </p>
      </div>

      {/* ── Pipeline summary card ─────────────────────────────── */}
      <div className="card mb-6">
        <div className="eyebrow">Generation pipeline</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
          <PipelineStep
            n={1} label="Generate"
            primary={data.model}
            secondary={`${data.resolution} · ${data.aspectRatio}`}
            note={`Renders the customer's brief on a solid ${data.backdropHex} backdrop`}
          />
          <PipelineStep
            n={2} label="Remove background"
            primary={data.bgRemovalModel}
            secondary={`Variant: ${data.bgRemovalVariant}`}
            note="Cuts the chroma backdrop to alpha, preserving anti-aliased edges"
          />
          <PipelineStep
            n={3} label="Persist"
            primary="files table"
            secondary="kind: 'artwork', PNG"
            note="Saved like a regular upload; same canvas + library + order flow"
          />
        </div>
      </div>

      {/* ── Usage leaderboard ────────────────────────────────── */}
      <div className="card mb-6">
        <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="eyebrow !mb-0">Top AI users</div>
            <p className="text-xs text-ink-3 mt-1">Ranked by total generations. Click any row to see the images they produced.</p>
          </div>
          {leader && (
            <div className="text-[11px] text-ink-3">
              <span className="text-ink font-bold font-mono">{leader.total.toLocaleString()}</span> generations · <span className="text-ink font-bold font-mono">{leader.users.length}</span> users
            </div>
          )}
        </div>
        {!leader ? (
          <div className="text-ink-3 text-sm">Loading…</div>
        ) : leader.users.length === 0 ? (
          <div className="text-ink-3 text-sm italic">No AI generations yet.</div>
        ) : (
          <div className="border border-border rounded overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">
                <tr>
                  <th className="px-3 py-2 text-left w-10">#</th>
                  <th className="px-3 py-2 text-left">Customer</th>
                  <th className="px-3 py-2 text-left hidden sm:table-cell">Email</th>
                  <th className="px-3 py-2 text-right">Generations</th>
                  <th className="px-3 py-2 text-right hidden md:table-cell">Last</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {leader.users.map((u, i) => (
                  <tr key={u.uploaderId ?? 'guest'}
                    onClick={() => openUser(u)}
                    className="border-t border-border hover:bg-brick-tint/40 cursor-pointer">
                    <td className="px-3 py-2 text-ink-3 font-mono text-xs">{i + 1}</td>
                    <td className="px-3 py-2 text-ink font-semibold">
                      {u.name}
                      {!u.uploaderId && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-amber-700">Guest</span>}
                    </td>
                    <td className="px-3 py-2 text-ink-2 text-xs hidden sm:table-cell">{u.email ?? '—'}</td>
                    <td className="px-3 py-2 text-right text-ink font-mono font-bold">{u.count}</td>
                    <td className="px-3 py-2 text-right text-ink-3 text-xs font-mono hidden md:table-cell">{u.lastAt ? new Date(u.lastAt).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2 text-ink-3">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Variant tabs ─────────────────────────────────────── */}
      <div className="card">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div>
            <div className="eyebrow !mb-0">Pre-configured system prompt</div>
            <p className="text-xs text-ink-3 mt-1">
              The customer's text replaces <code className="font-mono bg-surface px-1 py-0.5 rounded">&lt;the customer's text…&gt;</code> at the bottom. Garment colour switches the palette guidance — pick a tone to compare.
            </p>
          </div>
          <button onClick={copyPrompt}
            className="btn-outline text-xs px-3 py-1.5">
            {copied ? '✓ Copied' : 'Copy prompt'}
          </button>
        </div>

        <div className="flex gap-1 border-b border-border mb-3 flex-wrap">
          {(['dark', 'light', 'mid', 'pinned-palette'] as const).map((t) => {
            const v = data.variants.find((x) => x.tone === t)
            if (!v) return null
            const active = tab === t
            const label =
              t === 'pinned-palette' ? 'pinned palette + ref image' :
              `${t} garment`
            return (
              <button key={t} onClick={() => setTab(t)}
                className={[
                  'flex items-center gap-2 px-3 py-2 text-[11px] uppercase tracking-wider font-bold rounded-t border-b-2 -mb-px',
                  active ? 'border-brick text-brick bg-cream' : 'border-transparent text-ink-3 hover:text-brick hover:border-brick/40',
                ].join(' ')}>
                <span className="w-3 h-3 rounded-full border border-ink-4 inline-block"
                  style={{ background: v.example.garmentColourHex }} />
                {label}
              </button>
            )
          })}
        </div>

        {/* Example context block */}
        <div className="rounded border border-border bg-surface px-3 py-2 mb-3 text-[11px] text-ink-2 flex items-center gap-3 flex-wrap">
          <span className="font-bold uppercase tracking-wider text-ink-3 text-[10px]">Example used below</span>
          <span>Garment: <strong className="text-ink">{variant.example.garmentName}</strong></span>
          <span>Colour: <strong className="text-ink">{variant.example.garmentColourName}</strong> <span className="font-mono text-ink-3">({variant.example.garmentColourHex})</span></span>
        </div>

        <pre className="rounded border border-border bg-white text-[12.5px] text-ink-2 leading-relaxed p-4 overflow-auto whitespace-pre-wrap font-mono max-h-[60vh]">
          {variant.prompt}
        </pre>

        <p className="text-[11px] text-ink-3 mt-3">
          Edit this prompt in <code className="font-mono">apps/api/src/routes/files.ts</code> → <code className="font-mono">buildLogoPrompt()</code>. Changes take effect on the next API container restart; this page reflects the live route output without a code change here.
        </p>
      </div>

      {/* ── User AI gallery modal ────────────────────────────── */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedUser(null) }}>
          <div className="bg-cream rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-brick/40">
            <div className="bg-brick text-white px-5 py-3 flex items-center justify-between gap-3 shrink-0">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">AI gallery</div>
                <div className="text-sm font-semibold truncate">
                  {selectedUser.name}
                  {selectedUser.email && selectedUser.email !== selectedUser.name && (
                    <span className="ml-2 text-white/70 font-normal">— {selectedUser.email}</span>
                  )}
                  <span className="ml-3 font-mono">{selectedUser.count} generation{selectedUser.count === 1 ? '' : 's'}</span>
                </div>
              </div>
              <button onClick={() => setSelectedUser(null)}
                className="w-8 h-8 rounded hover:bg-white/15 flex items-center justify-center"
                aria-label="Close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="px-5 py-4 flex-1 overflow-y-auto bg-cream">
              {galleryLoading ? (
                <div className="text-ink-3 text-sm py-8 text-center">Loading…</div>
              ) : gallery.length === 0 ? (
                <div className="text-ink-3 text-sm py-8 text-center italic">No images.</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {gallery.map((f) => (
                    <a key={f.id}
                      href={apiSrc(f.url)} target="_blank" rel="noopener noreferrer"
                      className="group block border border-border rounded bg-white overflow-hidden hover:border-brick transition-colors">
                      <div className="aspect-square bg-[conic-gradient(at_50%_50%,#eee_25%,#fff_25%_50%,#eee_50%_75%,#fff_75%)] bg-[length:16px_16px] flex items-center justify-center">
                        <img src={apiSrc(f.url)} alt={f.originalName}
                          className="max-w-full max-h-full object-contain" />
                      </div>
                      <div className="px-2 py-2 text-[11px] leading-snug">
                        <div className="text-ink-2 line-clamp-2" title={f.aiPrompt ?? ''}>
                          {f.aiPrompt ?? <span className="italic text-ink-3">no prompt recorded</span>}
                        </div>
                        <div className="mt-1 flex items-center justify-between text-ink-3 font-mono text-[10px]">
                          <span>{f.widthPx ? `${f.widthPx} × ${f.heightPx}` : '—'}</span>
                          <span>{new Date(f.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PipelineStep({ n, label, primary, secondary, note }: { n: number; label: string; primary: string; secondary: string; note: string }) {
  return (
    <div className="border border-border rounded p-3 bg-surface">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-brick text-white text-xs font-bold">{n}</span>
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-brick">{label}</div>
      </div>
      <div className="mt-2 font-mono text-sm text-ink font-semibold break-all">{primary}</div>
      <div className="text-[11px] text-ink-3 font-mono mt-0.5">{secondary}</div>
      <div className="text-[11px] text-ink-2 mt-2 leading-snug">{note}</div>
    </div>
  )
}
